import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, adminHeaders } from "@/lib/api";

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("lm_admin"));
  const [pw, setPw] = useState("");
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [images, setImages] = useState([]);
  const [form, setForm] = useState({ url: "", title: "", category: "style", style: "", room: "" });

  const load = async () => {
    try {
      const [l, st, im] = await Promise.all([
        api.get("/admin/leads", { headers: adminHeaders() }),
        api.get("/admin/stats", { headers: adminHeaders() }),
        api.get("/images"),
      ]);
      setLeads(l.data); setStats(st.data); setImages(im.data);
    } catch {
      sessionStorage.removeItem("lm_admin"); setAuthed(false);
    }
  };
  useEffect(() => { if (authed) load(); }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/login", { password: pw });
      sessionStorage.setItem("lm_admin", pw); setAuthed(true);
    } catch { toast.error("Wrong password"); }
  };

  const addImage = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/images", { ...form, style: form.style || null, room: form.room || null }, { headers: adminHeaders() });
      toast.success("Image added"); setForm({ ...form, url: "", title: "" }); load();
    } catch { toast.error("Could not add image"); }
  };
  const del = async (id) => { await api.delete(`/admin/images/${id}`, { headers: adminHeaders() }); setImages(images.filter((i) => i.id !== id)); };

  if (!authed) return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={login} className="w-full max-w-sm">
        <p className="eyebrow">interiorsmoodboard studio</p>
        <h1 className="font-display text-4xl font-light mt-3">Welcome back.</h1>
        <input data-testid="admin-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Studio password" className="input-line mt-8 text-lg" />
        <button data-testid="admin-login-button" className="btn-ink mt-8 w-full">Enter</button>
        <Link to="/" className="block text-center text-xs text-neutral-400 mt-6">← Back to site</Link>
      </form>
    </main>
  );

  return (
    <main className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
      <div className="flex items-center justify-between">
        <div><p className="eyebrow">Studio</p><h1 className="font-display text-4xl font-light mt-2">Leads & taste boards</h1></div>
        <div className="flex gap-2">
          {["leads", "images"].map((t) => <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`chip capitalize ${tab === t ? "chip-on" : ""}`}>{t}</button>)}
          <button data-testid="admin-logout-button" onClick={() => { sessionStorage.removeItem("lm_admin"); setAuthed(false); }} className="chip">Log out</button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10">
          {[["Boards started", stats.sessions], ["Leads captured", stats.leads], ["Quote requests", stats.quotes], ["Images live", stats.images]].map(([l, v]) => (
            <div key={l} data-testid={`stat-${l.toLowerCase().replace(/\s/g, "-")}`} className="rounded-2xl border border-neutral-200 p-5"><div className="text-xs text-neutral-500">{l}</div><div className="font-display text-4xl font-light mt-2">{v}</div></div>
          ))}
        </div>
      )}

      {tab === "leads" ? (
        <div className="mt-10 overflow-x-auto">
          <table data-testid="leads-table" className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-400 border-b border-neutral-200">
              <th className="py-3 pr-4">Name</th><th className="py-3 pr-4">Phone</th><th className="py-3 pr-4">Project</th><th className="py-3 pr-4">Styles</th><th className="py-3 pr-4">Taste list</th><th className="py-3 pr-4">Quote</th><th className="py-3 pr-4">Updated</th><th /></tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="py-3 pr-4 font-medium">{l.contact?.name || <span className="text-neutral-300">anonymous</span>}</td>
                  <td className="py-3 pr-4 font-mono text-xs">{l.contact?.phone || "—"}</td>
                  <td className="py-3 pr-4 capitalize">{l.project_type || "—"} {l.project_name && <span className="text-neutral-400">· {l.project_name}</span>}</td>
                  <td className="py-3 pr-4 text-xs text-neutral-600">{Array.from(new Set(l.selected_styles.map((x) => x.style))).join(", ") || "—"}</td>
                  <td className="py-3 pr-4">{l.taste_list.length}</td>
                  <td className="py-3 pr-4">{l.quote_requested === true ? <span className="rounded-full bg-neutral-900 text-white text-[10px] px-2 py-1">YES</span> : l.quote_requested === false ? "no" : "—"}</td>
                  <td className="py-3 pr-4 text-xs text-neutral-400">{new Date(l.updated_at).toLocaleString()}</td>
                  <td className="py-3"><a data-testid={`lead-open-${l.id}`} href={`/b/${l.id}`} target="_blank" rel="noreferrer" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-300 hover:border-neutral-900"><ExternalLink size={13} /></a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-10">
          <form onSubmit={addImage} className="grid md:grid-cols-6 gap-3 items-end rounded-2xl border border-neutral-200 p-5">
            <input data-testid="admin-image-url" required placeholder="Image URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="md:col-span-2 h-11 rounded-full border border-neutral-300 px-4 text-sm" />
            <input data-testid="admin-image-title" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-full border border-neutral-300 px-4 text-sm" />
            <div className="flex rounded-full border border-neutral-300 p-0.5 h-11 text-xs">
              {["style", "furniture"].map((c) => <button type="button" key={c} data-testid={`admin-image-cat-${c}`} onClick={() => setForm({ ...form, category: c })} className={`flex-1 rounded-full capitalize ${form.category === c ? "bg-neutral-900 text-white" : ""}`}>{c}</button>)}
            </div>
            <input data-testid="admin-image-tag" placeholder={form.category === "style" ? "Style e.g. Japandi" : "Type e.g. Seating"} value={form.category === "style" ? form.style : form.room} onChange={(e) => setForm({ ...form, [form.category === "style" ? "style" : "room"]: e.target.value })} className="h-11 rounded-full border border-neutral-300 px-4 text-sm" />
            <button data-testid="admin-image-add-button" className="btn-ink h-11">Add image</button>
          </form>
          <div className="masonry columns-2 sm:columns-4 lg:columns-6 mt-8">
            {images.map((im) => (
              <div key={im.id} className="relative group rounded-xl overflow-hidden bg-neutral-100">
                <img src={im.url} alt={im.title} loading="lazy" className="w-full block" />
                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-2 py-1.5 flex justify-between items-center">
                  <span className="truncate">{im.style || im.room} · {im.title}</span>
                  <button data-testid={`admin-image-delete-${im.id}`} onClick={() => del(im.id)} className="opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
